import React, {useState} from 'react';
import axios from 'axios';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import {RootState} from '../../store';
import ProfileHeader from './profile/components/ProfileHeader';
import ProfileWarningIcon from './profile/components/ProfileWarningIcon';
import ProfileInformationSection from './profile/components/ProfileInformationSection';
import ProfileNameEditorModal, {
  PROFILE_NAME_MAX_LENGTH,
} from './profile/components/ProfileNameEditorModal';
import ProfileSocialLinksSection from './profile/components/ProfileSocialLinksSection';
import ProfileServicesSection from './profile/components/ProfileServicesSection';
import ProfileFeaturesSection from './profile/components/ProfileFeaturesSection';
import ProfileFacebookPageCreationModal from './profile/components/ProfileFacebookPageCreationModal';
import ProfilePictureEditorModal from './profile/components/ProfilePictureEditorModal';
import ProfilePictureSourceModal from './profile/components/ProfilePictureSourceModal';
import ProfileFacebookPagePickerModal from './profile/components/ProfileFacebookPagePickerModal';
import ProfileSocialConnectionModal from './profile/components/ProfileSocialConnectionModal';
import ProfileSafetyActionsModal from './profile/components/ProfileSafetyActionsModal';
import ProfileSafetySettingsModal from './profile/components/ProfileSafetySettingsModal';
import ProfileServiceEditorModal, {
  ProfileArtisticFormValues,
  ProfileAthleticFormValues,
  ProfileCollegeServiceFormValues,
  ProfileGeneralServiceFormValues,
  ProfileServiceEditorMode,
} from './profile/components/ProfileServiceEditorModal';
import {
  PROFILE_FEATURE_CARDS,
  PROFILE_TAB_OPTIONS,
  ProfileExperienceCard,
  ProfileExperienceCardRow,
  ProfileSocialConnectionRedirectResult,
  ProfileSocialConnectionStatus,
  ProfileSocialLink,
  ProfileSocialProvider,
  ProfileStat,
  ProfileTab,
} from './profile/constants';
import {
  ProfileApiArtisticExperience,
  ProfileApiArtisticExperienceInput,
  ProfileApiArtisticExperienceUpdate,
  ProfileApiAthleticService,
  ProfileApiAthleticServiceInput,
  ProfileApiAthleticServiceUpdate,
  ProfileApiCollegeService,
  ProfileApiCollegeServiceInput,
  ProfileApiCollegeServiceUpdate,
  ProfileApiError,
  ProfileApiFacebookPageOption,
  ProfileApiGeneralService,
  ProfileApiGeneralServiceInput,
  ProfileApiGeneralServiceUpdate,
  ProfileApiSocialConnection,
  ProfileApiSocialLinks,
  ProfileApiSocialProvider,
  ProfileApiUser,
  profileApi,
} from '../../services/profileApi';
import {
  SafetyBlockedUser,
  SafetyReportReason,
  safetyApi,
} from '../../services/safetyApi';
import {authApi} from '../../services/authApi';
import {isProPlanTier} from '../../utils/plan';
import {setUser} from '../../store/slices/userSlice';
import {
  createCroppedProfilePictureFile,
  loadImageSource,
  PROFILE_PICTURE_ALLOWED_TYPES,
  PROFILE_PICTURE_MAX_FILE_SIZE_BYTES,
  ProfilePictureCropState,
  readImageFileAsDataUrl,
} from './profile/utils/profilePicture';
import {getSocialProfileLabel} from './profile/utils/socialProfileLabel';
import styles from './profile/Profile.module.css';

const EMPTY_ATHLETIC_FORM_VALUES: ProfileAthleticFormValues = {
  sportName: '',
  skillLevel: '',
  yearsOfExperience: '',
  certifications: '',
  availability: '',
};

const EMPTY_ARTISTIC_FORM_VALUES: ProfileArtisticFormValues = {
  artForm: '',
  proficiencyLevel: '',
  yearsOfPractice: '',
  portfolioLinks: '',
  serviceRates: '',
};

const EMPTY_GENERAL_SERVICE_FORM_VALUES: ProfileGeneralServiceFormValues = {
  title: '',
  description: '',
  pricing: '',
  availability: '',
  contactPreferences: '',
};

const EMPTY_COLLEGE_SERVICE_FORM_VALUES: ProfileCollegeServiceFormValues = {
  opportunityType: '',
  title: '',
  description: '',
  eligibility: '',
  deadline: '',
  learnMoreUrl: '',
};

const MAX_SAFETY_BLOCKED_WORDS = 50;
const MAX_SAFETY_BLOCKED_WORD_LENGTH = 60;

type PayloadResult<T> =
  | {payload: T; error: null}
  | {payload: null; error: string};

type NormalizedAthleticValues = {
  sportName: string;
  skillLevel: string;
  yearsOfExperience: number | null;
  certifications: string[];
  availability: string;
};

type NormalizedArtisticValues = {
  artForm: string;
  proficiencyLevel: string;
  yearsOfPractice: number | null;
  portfolioLinks: string[];
  serviceRates: string;
};

type NormalizedGeneralServiceValues = {
  title: string;
  description: string;
  pricing: string;
  availability: string;
  contactPreferences: string;
};

type NormalizedCollegeServiceValues = {
  opportunityType: string;
  title: string;
  description: string;
  eligibility: string;
  deadline: string;
  learnMoreUrl: string;
};

type ProfilePictureEditorState = {
  file: File;
  previewUrl: string;
  imageWidth: number;
  imageHeight: number;
  cropState: ProfilePictureCropState;
};

const addRowIfPresent = (
  rows: ProfileExperienceCardRow[],
  label: string,
  value: string | number | null | undefined,
  accent: boolean = false
) => {
  if (value === undefined || value === null || value === '') {
    return;
  }

  rows.push({label, value: String(value), accent});
};

const formatYears = (years?: number) => {
  if (years === undefined || years === null) {
    return undefined;
  }

  return `${years} year${years === 1 ? '' : 's'}`;
};

const formatMemberSince = (createdAt?: string) => {
  if (!createdAt) {
    return 'N/A';
  }

  const parsedDate = new Date(createdAt);

  if (Number.isNaN(parsedDate.getTime())) {
    return 'N/A';
  }

  return parsedDate.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
};

const normalizeLinkLabel = (value: string) => value.replace(/^https?:\/\//, '');

const SOCIAL_PROVIDER_METADATA: Array<{
  provider: ProfileSocialProvider;
  label: string;
  icon: string;
  variant: ProfileSocialLink['variant'];
}> = [
  {provider: 'facebook', label: 'Facebook Page', icon: '📘', variant: 'facebook'},
  {provider: 'instagram', label: 'Instagram', icon: '📷', variant: 'instagram'},
  {provider: 'twitter', label: 'Twitter/X', icon: '🐦', variant: 'twitter'},
  {provider: 'linkedin', label: 'LinkedIn', icon: '💼', variant: 'linkedin'},
  {provider: 'youtube', label: 'YouTube', icon: '📺', variant: 'youtube'},
  {provider: 'tiktok', label: 'TikTok', icon: '🎵', variant: 'tiktok'},
  {provider: 'website', label: 'Website', icon: '🌐', variant: 'website'},
];

type SocialConnectionModalMode = 'website' | 'public-url';

const splitMultilineValues = (value: string) => value
  .split('\n')
  .map((entry) => entry.trim())
  .filter(Boolean);

const buildSafetyBlockedWords = (value: string): PayloadResult<string[]> => {
  const entries = splitMultilineValues(value)
    .map((entry) => entry.replace(/\s+/g, ' ').toLowerCase());

  if (entries.length > MAX_SAFETY_BLOCKED_WORDS) {
    return {
      payload: null,
      error: `Blocked words are limited to ${MAX_SAFETY_BLOCKED_WORDS} entries.`,
    };
  }

  const seenEntries = new Set<string>();

  for (const entry of entries) {
    if (entry.length > MAX_SAFETY_BLOCKED_WORD_LENGTH) {
      return {
        payload: null,
        error: `Blocked words must be ${MAX_SAFETY_BLOCKED_WORD_LENGTH} characters or fewer.`,
      };
    }

    if (seenEntries.has(entry)) {
      return {
        payload: null,
        error: 'Blocked words cannot include duplicate entries.',
      };
    }

    seenEntries.add(entry);
  }

  return {payload: entries, error: null};
};

const parseOptionalNonNegativeNumber = (value: string, label: string): PayloadResult<number | null> => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return {payload: null, error: null};
  }

  const parsedValue = Number(trimmedValue);
  if (!Number.isFinite(parsedValue) || parsedValue < 0) {
    return {payload: null, error: `${label} must be a non-negative number.`};
  }

  return {payload: parsedValue, error: null};
};

const getAthleticCardId = (service: ProfileApiAthleticService, index: number) =>
  service._id || `athletic-${index}-${service.sportName}`;

const getArtisticCardId = (experience: ProfileApiArtisticExperience, index: number) =>
  experience._id || `artistic-${index}-${experience.artForm}`;

const getGeneralServiceCardId = (service: ProfileApiGeneralService, index: number) =>
  service._id || `general-${index}-${service.title}`;

const getCollegeServiceCardId = (service: ProfileApiCollegeService, index: number) =>
  service._id || `college-${index}-${service.title}`;

const buildAthleticCards = (
  athleticServices: ProfileApiAthleticService[] = []
): ProfileExperienceCard[] => athleticServices.map((service, index) => {
  const rows: ProfileExperienceCardRow[] = [];

  addRowIfPresent(rows, 'Level', service.skillLevel);
  addRowIfPresent(rows, 'Experience', formatYears(service.yearsOfExperience));
  addRowIfPresent(rows, 'Availability', service.availability);
  addRowIfPresent(
    rows,
    'Certifications',
    service.certifications && service.certifications.length > 0 ? service.certifications.join(', ') : undefined
  );

  return {
    id: getAthleticCardId(service, index),
    title: service.sportName,
    rows,
  };
});

const buildArtisticCards = (
  artisticExperiences: ProfileApiArtisticExperience[] = []
): ProfileExperienceCard[] => artisticExperiences.map((experience, index) => {
  const rows: ProfileExperienceCardRow[] = [];

  addRowIfPresent(rows, 'Level', experience.proficiencyLevel);
  addRowIfPresent(rows, 'Active', formatYears(experience.yearsOfPractice));
  addRowIfPresent(rows, 'Rate', experience.serviceRates, true);
  addRowIfPresent(
    rows,
    'Portfolio',
    experience.portfolioLinks && experience.portfolioLinks.length > 0
      ? normalizeLinkLabel(experience.portfolioLinks[0])
      : undefined
  );

  return {
    id: getArtisticCardId(experience, index),
    title: experience.artForm,
    rows,
  };
});

const buildGeneralServiceCards = (
  generalServices: ProfileApiGeneralService[] = []
): ProfileExperienceCard[] => generalServices.map((service, index) => {
  const rows: ProfileExperienceCardRow[] = [];

  addRowIfPresent(rows, 'Description', service.description);
  addRowIfPresent(rows, 'Availability', service.availability);
  addRowIfPresent(rows, 'Rate', service.pricing, true);
  addRowIfPresent(rows, 'Contact', service.contactPreferences);

  return {
    id: getGeneralServiceCardId(service, index),
    title: service.title,
    rows,
  };
});

const buildCollegeServiceCards = (
  collegeServices: ProfileApiCollegeService[] = []
): ProfileExperienceCard[] => collegeServices.map((service, index) => {
  const rows: ProfileExperienceCardRow[] = [];

  addRowIfPresent(rows, 'Opportunity', service.opportunityType);
  addRowIfPresent(rows, 'Description', service.description);
  addRowIfPresent(rows, 'Eligibility', service.eligibility);
  addRowIfPresent(rows, 'Deadline', service.deadline);
  addRowIfPresent(
    rows,
    'Learn More',
    service.learnMoreUrl ? normalizeLinkLabel(service.learnMoreUrl) : undefined,
    true
  );

  return {
    id: getCollegeServiceCardId(service, index),
    title: service.title,
    rows,
  };
});

const getSocialProviderLabel = (provider: ProfileSocialProvider) =>
  SOCIAL_PROVIDER_METADATA.find((item) => item.provider === provider)?.label || provider;

const isSupportedSocialProvider = (value: string): value is ProfileSocialProvider =>
  SOCIAL_PROVIDER_METADATA.some((item) => item.provider === value);

const resolveSocialConnectionStatus = (
  provider: ProfileSocialProvider,
  connection: ProfileApiSocialConnection | undefined,
  socialLinks: ProfileApiSocialLinks | undefined,
): {
  href?: string;
  status: ProfileSocialConnectionStatus;
  statusLabel: string;
  primaryActionLabel: string;
  helperText: string;
  canDisconnect: boolean;
} => {
  const href = connection?.profileUrl || socialLinks?.[provider];
  const status = connection?.status || (href ? 'connected' : 'disconnected');

  switch (status) {
    case 'connected':
      return {
        href,
        status,
        statusLabel: 'Connected',
        primaryActionLabel: provider === 'website' ? 'Visit Website' : 'View Profile',
        helperText: getSocialProfileLabel(provider, connection, href),
        canDisconnect: true,
      };
    case 'needs_public_url':
      if (provider === 'linkedin') {
        return {
          href,
          status: 'failed',
          statusLabel: 'Connection Failed',
          primaryActionLabel: 'Try Again',
          helperText: connection?.lastError || 'LinkedIn could not provide a public profile URL. Reconnect to try again.',
          canDisconnect: Boolean(href || connection?.providerAccountId || connection?.connectionMethod),
        };
      }

      return {
        href,
        status,
        statusLabel: 'Needs URL Confirmation',
        primaryActionLabel: provider === 'website' ? 'Add Link' : 'Finish Setup',
        helperText: connection?.lastError || `Add your public ${getSocialProviderLabel(provider)} URL to finish linking.`,
        canDisconnect: true,
      };
    case 'failed':
      return {
        href,
        status,
        statusLabel: 'Connection Failed',
        primaryActionLabel: provider === 'website' ? 'Add Link' : 'Try Again',
        helperText: connection?.lastError || `Reconnect ${getSocialProviderLabel(provider)} to try again.`,
        canDisconnect: Boolean(href || connection?.providerAccountId || connection?.connectionMethod),
      };
    case 'disconnected':
    default:
      return {
        href,
        status: 'disconnected',
        statusLabel: 'Not Connected',
        primaryActionLabel: provider === 'website' ? 'Add Link' : 'Connect',
        helperText: provider === 'website'
          ? 'Add your public website URL.'
          : provider === 'facebook'
            ? 'Sign in with Facebook to connect a Page you manage.'
          : provider === 'instagram'
            ? 'Sign in with Instagram to link a Creator or Business account.'
            : provider === 'linkedin'
              ? 'Sign in with LinkedIn to connect your public profile automatically.'
            : `Sign in with ${getSocialProviderLabel(provider)} to link this account.`,
        canDisconnect: false,
      };
  }
};

const buildSocialLinks = (
  socialConnections?: ProfileApiUser['socialConnections'],
  socialLinks?: ProfileApiSocialLinks,
): ProfileSocialLink[] => SOCIAL_PROVIDER_METADATA.map(({provider, label, icon, variant}) => {
  const resolvedConnection = resolveSocialConnectionStatus(provider, socialConnections?.[provider], socialLinks);

  return {
    provider,
    label,
    icon,
    variant,
    href: resolvedConnection.href,
    status: resolvedConnection.status,
    statusLabel: resolvedConnection.statusLabel,
    primaryActionLabel: resolvedConnection.primaryActionLabel,
    helperText: resolvedConnection.helperText,
    canDisconnect: resolvedConnection.canDisconnect,
  };
});

const buildProfileStats = (profile: ProfileApiUser | null): ProfileStat[] => {
  const athleticCount = profile?.athleticServices?.length || 0;
  const artisticCount = profile?.artisticExperiences?.length || 0;
  const servicesCount = profile?.generalServices?.length || 0;
  const collegeCount = profile?.collegeServices?.length || 0;

  return [
    {label: 'Followers', value: profile?.stats?.followerCount || 0},
    {label: 'Following', value: profile?.stats?.followingCount || 0},
    {label: 'Listings', value: athleticCount + artisticCount + servicesCount + collegeCount},
  ];
};

const mapAthleticServiceToFormValues = (
  service?: ProfileApiAthleticService
): ProfileAthleticFormValues => ({
  sportName: service?.sportName || '',
  skillLevel: service?.skillLevel || '',
  yearsOfExperience: service?.yearsOfExperience !== undefined ? String(service.yearsOfExperience) : '',
  certifications: service?.certifications?.join('\n') || '',
  availability: service?.availability || '',
});

const mapArtisticExperienceToFormValues = (
  experience?: ProfileApiArtisticExperience
): ProfileArtisticFormValues => ({
  artForm: experience?.artForm || '',
  proficiencyLevel: experience?.proficiencyLevel || '',
  yearsOfPractice: experience?.yearsOfPractice !== undefined ? String(experience.yearsOfPractice) : '',
  portfolioLinks: experience?.portfolioLinks?.join('\n') || '',
  serviceRates: experience?.serviceRates || '',
});

const mapGeneralServiceToFormValues = (
  service?: ProfileApiGeneralService
): ProfileGeneralServiceFormValues => ({
  title: service?.title || '',
  description: service?.description || '',
  pricing: service?.pricing || '',
  availability: service?.availability || '',
  contactPreferences: service?.contactPreferences || '',
});

const mapCollegeServiceToFormValues = (
  service?: ProfileApiCollegeService
): ProfileCollegeServiceFormValues => ({
  opportunityType: service?.opportunityType || '',
  title: service?.title || '',
  description: service?.description || '',
  eligibility: service?.eligibility || '',
  deadline: service?.deadline || '',
  learnMoreUrl: service?.learnMoreUrl || '',
});

const buildAthleticValues = (
  values: ProfileAthleticFormValues
): PayloadResult<NormalizedAthleticValues> => {
  const sportName = values.sportName.trim();
  const skillLevel = values.skillLevel.trim();

  if (!sportName) {
    return {payload: null, error: 'Sport name is required.'};
  }

  if (!skillLevel) {
    return {payload: null, error: 'Skill level is required.'};
  }

  const yearsResult = parseOptionalNonNegativeNumber(values.yearsOfExperience, 'Years of experience');
  if (yearsResult.error) {
    return {payload: null, error: yearsResult.error};
  }

  return {
    payload: {
      sportName,
      skillLevel,
      yearsOfExperience: yearsResult.payload,
      certifications: splitMultilineValues(values.certifications),
      availability: values.availability.trim(),
    },
    error: null,
  };
};

const buildArtisticValues = (
  values: ProfileArtisticFormValues
): PayloadResult<NormalizedArtisticValues> => {
  const artForm = values.artForm.trim();
  const proficiencyLevel = values.proficiencyLevel.trim();

  if (!artForm) {
    return {payload: null, error: 'Art form is required.'};
  }

  if (!proficiencyLevel) {
    return {payload: null, error: 'Proficiency level is required.'};
  }

  const yearsResult = parseOptionalNonNegativeNumber(values.yearsOfPractice, 'Years of practice');
  if (yearsResult.error) {
    return {payload: null, error: yearsResult.error};
  }

  return {
    payload: {
      artForm,
      proficiencyLevel,
      yearsOfPractice: yearsResult.payload,
      portfolioLinks: splitMultilineValues(values.portfolioLinks),
      serviceRates: values.serviceRates.trim(),
    },
    error: null,
  };
};

const buildGeneralServiceValues = (
  values: ProfileGeneralServiceFormValues
): PayloadResult<NormalizedGeneralServiceValues> => {
  const title = values.title.trim();
  const description = values.description.trim();

  if (!title) {
    return {payload: null, error: 'Service title is required.'};
  }

  if (!description) {
    return {payload: null, error: 'Service description is required.'};
  }

  return {
    payload: {
      title,
      description,
      pricing: values.pricing.trim(),
      availability: values.availability.trim(),
      contactPreferences: values.contactPreferences.trim(),
    },
    error: null,
  };
};

const buildCollegeServiceValues = (
  values: ProfileCollegeServiceFormValues
): PayloadResult<NormalizedCollegeServiceValues> => {
  const opportunityType = values.opportunityType.trim();
  const title = values.title.trim();
  const description = values.description.trim();
  const learnMoreUrl = values.learnMoreUrl.trim();
  const deadline = values.deadline.trim();

  if (!opportunityType) {
    return {payload: null, error: 'Opportunity type is required.'};
  }

  if (!title) {
    return {payload: null, error: 'Opportunity title is required.'};
  }

  if (!description) {
    return {payload: null, error: 'Opportunity description is required.'};
  }

  if (deadline) {
    const parsedDeadline = new Date(`${deadline}T00:00:00.000Z`);
    if (
      Number.isNaN(parsedDeadline.getTime()) ||
      parsedDeadline.toISOString().slice(0, 10) !== deadline
    ) {
      return {payload: null, error: 'Deadline must be a valid date.'};
    }
  }

  if (learnMoreUrl) {
    try {
      const parsedUrl = new URL(learnMoreUrl);
      if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
        return {payload: null, error: 'Learn more URL must start with http:// or https://.'};
      }
    } catch {
      return {payload: null, error: 'Learn more URL must be a valid URL.'};
    }
  }

  return {
    payload: {
      opportunityType,
      title,
      description,
      eligibility: values.eligibility.trim(),
      deadline,
      learnMoreUrl,
    },
    error: null,
  };
};

const toAthleticCreatePayload = (values: NormalizedAthleticValues): ProfileApiAthleticServiceInput => {
  const payload: ProfileApiAthleticServiceInput = {
    sportName: values.sportName,
    skillLevel: values.skillLevel,
  };

  if (values.yearsOfExperience !== null) {
    payload.yearsOfExperience = values.yearsOfExperience;
  }

  if (values.certifications.length > 0) {
    payload.certifications = values.certifications;
  }

  if (values.availability) {
    payload.availability = values.availability;
  }

  return payload;
};

const toAthleticUpdatePayload = (values: NormalizedAthleticValues): ProfileApiAthleticServiceUpdate => ({
  sportName: values.sportName,
  skillLevel: values.skillLevel,
  yearsOfExperience: values.yearsOfExperience,
  certifications: values.certifications,
  availability: values.availability,
});

const toArtisticCreatePayload = (values: NormalizedArtisticValues): ProfileApiArtisticExperienceInput => {
  const payload: ProfileApiArtisticExperienceInput = {
    artForm: values.artForm,
    proficiencyLevel: values.proficiencyLevel,
  };

  if (values.yearsOfPractice !== null) {
    payload.yearsOfPractice = values.yearsOfPractice;
  }

  if (values.portfolioLinks.length > 0) {
    payload.portfolioLinks = values.portfolioLinks;
  }

  if (values.serviceRates) {
    payload.serviceRates = values.serviceRates;
  }

  return payload;
};

const toArtisticUpdatePayload = (values: NormalizedArtisticValues): ProfileApiArtisticExperienceUpdate => ({
  artForm: values.artForm,
  proficiencyLevel: values.proficiencyLevel,
  yearsOfPractice: values.yearsOfPractice,
  portfolioLinks: values.portfolioLinks,
  serviceRates: values.serviceRates,
});

const toGeneralServiceCreatePayload = (values: NormalizedGeneralServiceValues): ProfileApiGeneralServiceInput => {
  const payload: ProfileApiGeneralServiceInput = {
    title: values.title,
    description: values.description,
  };

  if (values.pricing) {
    payload.pricing = values.pricing;
  }

  if (values.availability) {
    payload.availability = values.availability;
  }

  if (values.contactPreferences) {
    payload.contactPreferences = values.contactPreferences;
  }

  return payload;
};

const toGeneralServiceUpdatePayload = (values: NormalizedGeneralServiceValues): ProfileApiGeneralServiceUpdate => ({
  title: values.title,
  description: values.description,
  pricing: values.pricing,
  availability: values.availability,
  contactPreferences: values.contactPreferences,
});

const toCollegeServiceCreatePayload = (values: NormalizedCollegeServiceValues): ProfileApiCollegeServiceInput => {
  const payload: ProfileApiCollegeServiceInput = {
    opportunityType: values.opportunityType,
    title: values.title,
    description: values.description,
  };

  if (values.eligibility) {
    payload.eligibility = values.eligibility;
  }

  if (values.deadline) {
    payload.deadline = values.deadline;
  }

  if (values.learnMoreUrl) {
    payload.learnMoreUrl = values.learnMoreUrl;
  }

  return payload;
};

const toCollegeServiceUpdatePayload = (values: NormalizedCollegeServiceValues): ProfileApiCollegeServiceUpdate => ({
  opportunityType: values.opportunityType,
  title: values.title,
  description: values.description,
  eligibility: values.eligibility,
  deadline: values.deadline,
  learnMoreUrl: values.learnMoreUrl,
});

const getMutationErrorMessage = (error: unknown, fallbackMessage: string) =>
  error instanceof Error ? error.message : fallbackMessage;

const getSocialConnectionModalCopy = (
  mode: SocialConnectionModalMode,
  provider: ProfileSocialProvider | null
) => {
  const providerLabel = provider ? getSocialProviderLabel(provider) : 'social account';

  if (mode === 'website') {
    return {
      title: 'Add Website Link',
      description: 'Save the public website URL that visitors should open from your Profile page.',
      submitLabel: 'Save Website',
    };
  }

  return {
    title: `Finish ${providerLabel} Setup`,
    description: `We verified your ${providerLabel} account. Add the public profile URL that should open from your Profile page.`,
    submitLabel: 'Save Link',
  };
};

const validateSocialProfileUrl = (
  provider: ProfileSocialProvider,
  value: string
): string | null => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return 'A public profile URL is required.';
  }

  let parsedUrl: URL;

  try {
    parsedUrl = new URL(trimmedValue);
  } catch (error) {
    return 'Please enter a valid URL starting with https://';
  }

  if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
    return 'Please enter a valid URL starting with https://';
  }

  if (provider === 'website' && parsedUrl.protocol !== 'https:') {
    return 'Website links must use HTTPS.';
  }

  return null;
};

const Profile: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const token = useSelector((state: RootState) => state.auth.token);
  const user = useSelector((state: RootState) => state.user.currentUser);
  const requestedProfileUserId = React.useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const userId = searchParams.get('userId')?.trim();

    return userId || null;
  }, [location.search]);
  const isOwnProfile = !requestedProfileUserId || requestedProfileUserId === user?.id;
  const profilePictureCameraInputRef = React.useRef<HTMLInputElement | null>(null);
  const profilePictureLibraryInputRef = React.useRef<HTMLInputElement | null>(null);
  const profilePictureRemoveInFlightRef = React.useRef(false);
  const profileNameSaveInFlightRef = React.useRef(false);
  const [activeTab, setActiveTab] = useState<ProfileTab>('athletic');
  const [profile, setProfile] = useState<ProfileApiUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isProfileNameEditorOpen, setIsProfileNameEditorOpen] = useState(false);
  const [profileNameEditorValue, setProfileNameEditorValue] = useState('');
  const [profileNameSaving, setProfileNameSaving] = useState(false);
  const [profileNameError, setProfileNameError] = useState<string | null>(null);
  const [profileNameValidationError, setProfileNameValidationError] = useState<string | null>(null);
  const [profileNameSuccess, setProfileNameSuccess] = useState<string | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editorTab, setEditorTab] = useState<ProfileTab>('athletic');
  const [editorMode, setEditorMode] = useState<ProfileServiceEditorMode>('create');
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [athleticFormValues, setAthleticFormValues] = useState<ProfileAthleticFormValues>(EMPTY_ATHLETIC_FORM_VALUES);
  const [artisticFormValues, setArtisticFormValues] = useState<ProfileArtisticFormValues>(EMPTY_ARTISTIC_FORM_VALUES);
  const [generalServiceFormValues, setGeneralServiceFormValues] = useState<ProfileGeneralServiceFormValues>(
    EMPTY_GENERAL_SERVICE_FORM_VALUES
  );
  const [collegeServiceFormValues, setCollegeServiceFormValues] = useState<ProfileCollegeServiceFormValues>(
    EMPTY_COLLEGE_SERVICE_FORM_VALUES
  );
  const [managerLoading, setManagerLoading] = useState(false);
  const [managerError, setManagerError] = useState<string | null>(null);
  const [editorError, setEditorError] = useState<string | null>(null);
  const [editorValidationError, setEditorValidationError] = useState<string | null>(null);
  const [socialActionLoadingProvider, setSocialActionLoadingProvider] = useState<ProfileSocialProvider | null>(null);
  const [socialActionError, setSocialActionError] = useState<string | null>(null);
  const [socialActionSuccess, setSocialActionSuccess] = useState<string | null>(null);
  const [isSocialConnectionModalOpen, setIsSocialConnectionModalOpen] = useState(false);
  const [socialConnectionModalMode, setSocialConnectionModalMode] = useState<SocialConnectionModalMode>('website');
  const [socialConnectionModalProvider, setSocialConnectionModalProvider] = useState<ProfileSocialProvider | null>(null);
  const [socialConnectionModalValue, setSocialConnectionModalValue] = useState('');
  const [socialConnectionModalError, setSocialConnectionModalError] = useState<string | null>(null);
  const [socialConnectionModalValidationError, setSocialConnectionModalValidationError] = useState<string | null>(null);
  const [socialConnectionModalLoading, setSocialConnectionModalLoading] = useState(false);
  const [isFacebookPageCreationModalOpen, setIsFacebookPageCreationModalOpen] = useState(false);
  const [facebookPageCreationModalDescription, setFacebookPageCreationModalDescription] = useState(
    'A Facebook Page is required to connect here. Create one, then try again.'
  );
  const [facebookPageCreationModalError, setFacebookPageCreationModalError] = useState<string | null>(null);
  const [isFacebookPagePickerOpen, setIsFacebookPagePickerOpen] = useState(false);
  const [facebookPagePickerState, setFacebookPagePickerState] = useState<string | null>(null);
  const [facebookPageOptions, setFacebookPageOptions] = useState<ProfileApiFacebookPageOption[]>([]);
  const [facebookPagePickerLoading, setFacebookPagePickerLoading] = useState(false);
  const [facebookPagePickerSubmittingPageId, setFacebookPagePickerSubmittingPageId] = useState<string | null>(null);
  const [facebookPagePickerError, setFacebookPagePickerError] = useState<string | null>(null);
  const [profilePictureLoading, setProfilePictureLoading] = useState(false);
  const [profilePictureError, setProfilePictureError] = useState<string | null>(null);
  const [profilePictureSuccess, setProfilePictureSuccess] = useState<string | null>(null);
  const [profilePictureEditor, setProfilePictureEditor] = useState<ProfilePictureEditorState | null>(null);
  const [isProfilePictureSourceOpen, setIsProfilePictureSourceOpen] = useState(false);
  const [isSafetySettingsOpen, setIsSafetySettingsOpen] = useState(false);
  const [safetySettingsLoading, setSafetySettingsLoading] = useState(false);
  const [safetySettingsSaving, setSafetySettingsSaving] = useState(false);
  const [safetySettingsError, setSafetySettingsError] = useState<string | null>(null);
  const [safetySettingsValidationError, setSafetySettingsValidationError] = useState<string | null>(null);
  const [safetySettingsSuccess, setSafetySettingsSuccess] = useState<string | null>(null);
  const [safetyBlockedWordsText, setSafetyBlockedWordsText] = useState('');
  const [safetyBlockObscenity, setSafetyBlockObscenity] = useState(false);
  const [safetyLocationSharingEnabled, setSafetyLocationSharingEnabled] = useState(true);
  const [safetyBlockedUsers, setSafetyBlockedUsers] = useState<SafetyBlockedUser[]>([]);
  const [safetyUnblockingUserId, setSafetyUnblockingUserId] = useState<string | null>(null);
  const [isSafetyActionsOpen, setIsSafetyActionsOpen] = useState(false);
  const [safetyActionBlocking, setSafetyActionBlocking] = useState(false);
  const [safetyActionReporting, setSafetyActionReporting] = useState(false);
  const [safetyActionError, setSafetyActionError] = useState<string | null>(null);
  const [safetyActionSuccess, setSafetyActionSuccess] = useState<string | null>(null);
  const [safetyReportReason, setSafetyReportReason] = useState<SafetyReportReason>('harassment');
  const [safetyReportDetails, setSafetyReportDetails] = useState('');
  const canManageCollegeServices = isOwnProfile && Boolean(user?.enterprise);
  const hasCollegeServices = Boolean(profile?.collegeServices?.length);
  const shouldShowCollegeTab = isOwnProfile ? canManageCollegeServices : hasCollegeServices;
  const availableProfileTabs = React.useMemo(
    () => PROFILE_TAB_OPTIONS.filter((tabOption) => (
      tabOption.id !== 'college' || shouldShowCollegeTab
    )),
    [shouldShowCollegeTab]
  );

  React.useEffect(() => {
    if (availableProfileTabs.some((tabOption) => tabOption.id === activeTab)) {
      return;
    }

    setActiveTab(availableProfileTabs[0]?.id || 'athletic');
  }, [activeTab, availableProfileTabs]);

  React.useEffect(() => {
    if (!token) {
      setProfile(null);
      setError('You must be signed in to view this profile.');
      setLoading(false);
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    const loadProfile = async () => {
      setLoading(true);
      setError(null);

      if (!isOwnProfile && !requestedProfileUserId) {
        setProfile(null);
        setError('User profile not found.');
        setLoading(false);
        return;
      }

      try {
        let nextProfile: ProfileApiUser;

        if (isOwnProfile) {
          nextProfile = await profileApi.getMyProfile(token, controller.signal);
        } else {
          nextProfile = await profileApi.getProfile(requestedProfileUserId, controller.signal);
        }

        if (!isMounted) {
          return;
        }

        setProfile(nextProfile);
      } catch (loadError) {
        if (axios.isAxiosError(loadError) && loadError.code === 'ERR_CANCELED') {
          return;
        }

        if (!isMounted) {
          return;
        }

        setProfile(null);
        setError(loadError instanceof Error ? loadError.message : 'Failed to load profile');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    void loadProfile();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [isOwnProfile, requestedProfileUserId, retryCount, token]);

  React.useEffect(() => {
    if (!isOwnProfile || !location.search) {
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const rawResult = searchParams.get('socialConnectionResult');
    const result = rawResult as ProfileSocialConnectionRedirectResult | null;
    const provider = searchParams.get('socialConnectionProvider');
    const message = searchParams.get('socialConnectionMessage');
    const needsPublicUrl = searchParams.get('needsPublicUrl') === '1';
    const socialConnectionState = searchParams.get('socialConnectionState');

    if (!result && !provider) {
      return;
    }

    if (result === 'connected') {
      resetFacebookPageCreationModal();
      resetFacebookPagePickerSession();
      setSocialActionError(null);
      setSocialActionSuccess(message || (provider && isSupportedSocialProvider(provider)
        ? `${getSocialProviderLabel(provider)} linked successfully.`
        : 'Social account linked successfully.'));
    } else if (result === 'disconnected') {
      resetFacebookPageCreationModal();
      resetFacebookPagePickerSession();
      setSocialActionError(null);
      setSocialActionSuccess(message || 'Social account disconnected.');
    } else if (result === 'failed') {
      resetFacebookPageCreationModal();
      resetFacebookPagePickerSession();
      setSocialActionSuccess(null);
      setSocialActionError(message || 'We could not complete that social account link.');
    } else if (result === 'needs_public_url') {
      resetFacebookPageCreationModal();
      resetFacebookPagePickerSession();
      if (provider === 'linkedin') {
        setSocialActionSuccess(null);
        setSocialActionError(message || 'LinkedIn did not return a public profile URL for this account. Please try again.');
      } else {
        setSocialActionError(null);
        setSocialActionSuccess(message || 'Finish setup by adding your public profile URL.');
      }

      if (provider && provider !== 'linkedin' && isSupportedSocialProvider(provider) && needsPublicUrl) {
        setSocialConnectionModalMode(provider === 'website' ? 'website' : 'public-url');
        setSocialConnectionModalProvider(provider);
        setSocialConnectionModalValue('');
        setSocialConnectionModalError(null);
        setSocialConnectionModalValidationError(null);
        setIsSocialConnectionModalOpen(true);
      }
    } else if (result === 'needs_page_selection') {
      resetFacebookPageCreationModal();
      setSocialActionError(null);
      setSocialActionSuccess(message || 'Choose the Facebook Page you want to connect.');

      if (provider === 'facebook' && socialConnectionState) {
        setFacebookPagePickerState(socialConnectionState);
        setFacebookPageOptions([]);
        setFacebookPagePickerError(null);
        setFacebookPagePickerSubmittingPageId(null);
        setIsFacebookPagePickerOpen(true);
      } else {
        setSocialActionSuccess(null);
        setSocialActionError(message || 'We could not load the Facebook Page selection flow.');
      }
    } else if (result === 'needs_page_creation') {
      resetFacebookPagePickerSession();
      setIsSocialConnectionModalOpen(false);
      setSocialConnectionModalProvider(null);
      setSocialConnectionModalValue('');
      setSocialConnectionModalError(null);
      setSocialConnectionModalValidationError(null);
      setSocialActionError(null);
      setSocialActionSuccess(null);
      setFacebookPageCreationModalDescription(
        message || 'A Facebook Page is required to connect here. Create one, then try again.'
      );
      setFacebookPageCreationModalError(null);
      setIsFacebookPageCreationModalOpen(true);
    }

    searchParams.delete('socialConnectionResult');
    searchParams.delete('socialConnectionProvider');
    searchParams.delete('socialConnectionMessage');
    searchParams.delete('needsPublicUrl');
    searchParams.delete('socialConnectionState');

    const nextSearch = searchParams.toString();
    navigate({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : '',
    }, {replace: true});
  }, [isOwnProfile, location.pathname, location.search, navigate]);

  const resetEditorMessages = () => {
    setEditorError(null);
    setEditorValidationError(null);
  };

  const resetSocialModalMessages = () => {
    setSocialConnectionModalError(null);
    setSocialConnectionModalValidationError(null);
  };

  const resetFacebookPageCreationModal = () => {
    setIsFacebookPageCreationModalOpen(false);
    setFacebookPageCreationModalDescription('A Facebook Page is required to connect here. Create one, then try again.');
    setFacebookPageCreationModalError(null);
  };

  const closeFacebookPageCreationModal = () => {
    if (socialActionLoadingProvider === 'facebook') {
      return;
    }

    resetFacebookPageCreationModal();
  };

  const resetFacebookPagePickerSession = () => {
    setIsFacebookPagePickerOpen(false);
    setFacebookPagePickerState(null);
    setFacebookPageOptions([]);
    setFacebookPagePickerLoading(false);
    setFacebookPagePickerSubmittingPageId(null);
    setFacebookPagePickerError(null);
  };

  const closeFacebookPagePickerModal = () => {
    if (facebookPagePickerLoading || facebookPagePickerSubmittingPageId) {
      return;
    }

    setIsFacebookPagePickerOpen(false);
    setFacebookPagePickerError(null);
  };

  const forceCloseSocialConnectionModal = () => {
    setIsSocialConnectionModalOpen(false);
    setSocialConnectionModalProvider(null);
    setSocialConnectionModalValue('');
    resetSocialModalMessages();
  };

  const closeSocialConnectionModal = () => {
    if (socialConnectionModalLoading) {
      return;
    }

    forceCloseSocialConnectionModal();
  };

  React.useEffect(() => {
    if (!isOwnProfile || !isFacebookPagePickerOpen || !facebookPagePickerState) {
      return;
    }

    if (!token) {
      setFacebookPagePickerLoading(false);
      setFacebookPagePickerError('You must be signed in to finish connecting your Facebook Page.');
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    setFacebookPagePickerLoading(true);
    setFacebookPagePickerError(null);

    void profileApi.getFacebookPageOptions(token, facebookPagePickerState, controller.signal)
      .then((pages) => {
        if (!isMounted) {
          return;
        }

        setFacebookPageOptions(pages);
      })
      .catch((loadError) => {
        if (axios.isAxiosError(loadError) && loadError.code === 'ERR_CANCELED') {
          return;
        }

        if (!isMounted) {
          return;
        }

        const errorMessage = getMutationErrorMessage(loadError, 'Failed to load your Facebook Pages.');

        if (loadError instanceof ProfileApiError && loadError.status === 404) {
          setSocialActionSuccess(null);
          setSocialActionError(errorMessage);
          resetFacebookPagePickerSession();
          return;
        }

        setFacebookPageOptions([]);
        setFacebookPagePickerError(errorMessage);
      })
      .finally(() => {
        if (isMounted) {
          setFacebookPagePickerLoading(false);
        }
      });

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [facebookPagePickerState, isFacebookPagePickerOpen, isOwnProfile, token]);

  React.useEffect(() => {
    if (isOwnProfile) {
      return;
    }

    setIsEditorOpen(false);
    setEditingItemId(null);
    setIsProfileNameEditorOpen(false);
    setProfileNameEditorValue('');
    setProfileNameSaving(false);
    setProfileNameError(null);
    setProfileNameValidationError(null);
    setProfileNameSuccess(null);
    setManagerError(null);
    setEditorError(null);
    setEditorValidationError(null);
    setSocialActionLoadingProvider(null);
    setSocialActionError(null);
    setSocialActionSuccess(null);
    setIsSocialConnectionModalOpen(false);
    setSocialConnectionModalProvider(null);
    setSocialConnectionModalValue('');
    setSocialConnectionModalError(null);
    setSocialConnectionModalValidationError(null);
    resetFacebookPageCreationModal();
    resetFacebookPagePickerSession();
    setProfilePictureEditor(null);
    setIsProfilePictureSourceOpen(false);
    setProfilePictureError(null);
    setProfilePictureSuccess(null);
    setIsSafetySettingsOpen(false);
    setSafetySettingsLoading(false);
    setSafetySettingsSaving(false);
    setSafetySettingsError(null);
    setSafetySettingsValidationError(null);
    setSafetySettingsSuccess(null);
    setSafetyUnblockingUserId(null);
  }, [isOwnProfile]);

  React.useEffect(() => {
    if (!isOwnProfile) {
      return;
    }

    setIsSafetyActionsOpen(false);
    setSafetyActionBlocking(false);
    setSafetyActionReporting(false);
    setSafetyActionError(null);
    setSafetyActionSuccess(null);
    setSafetyReportDetails('');
    setSafetyReportReason('harassment');
  }, [isOwnProfile]);

  const openSocialConnectionModal = (
    provider: ProfileSocialProvider,
    mode: SocialConnectionModalMode,
    initialValue: string = ''
  ) => {
    setSocialActionError(null);
    setSocialActionSuccess(null);
    setSocialConnectionModalMode(mode);
    setSocialConnectionModalProvider(provider);
    setSocialConnectionModalValue(initialValue);
    resetSocialModalMessages();
    setIsSocialConnectionModalOpen(true);
  };

  const resetFormForTab = (tab: ProfileTab) => {
    if (tab === 'athletic') {
      setAthleticFormValues({...EMPTY_ATHLETIC_FORM_VALUES});
      return;
    }

    if (tab === 'artistic') {
      setArtisticFormValues({...EMPTY_ARTISTIC_FORM_VALUES});
      return;
    }

    if (tab === 'services') {
      setGeneralServiceFormValues({...EMPTY_GENERAL_SERVICE_FORM_VALUES});
      return;
    }

    setCollegeServiceFormValues({...EMPTY_COLLEGE_SERVICE_FORM_VALUES});
  };

  const closeEditor = () => {
    setIsEditorOpen(false);
    setEditingItemId(null);
    resetEditorMessages();
  };

  const startSocialConnectionRedirect = async (
    provider: ProfileSocialProvider,
    options?: {
      onError?: (message: string) => void;
    }
  ) => {
    if (!token) {
      const errorMessage = 'You must be signed in to connect social accounts.';
      setSocialActionSuccess(null);
      setSocialActionError(errorMessage);
      options?.onError?.(errorMessage);
      return;
    }

    let didRedirect = false;
    setSocialActionLoadingProvider(provider);
    setSocialActionError(null);
    setSocialActionSuccess(null);

    try {
      const authorizeUrl = await profileApi.startSocialConnection(token, provider, {
        returnPath: location.pathname,
      });

      didRedirect = true;
      window.location.assign(authorizeUrl);
    } catch (socialError) {
      const errorMessage = getMutationErrorMessage(socialError, `Failed to connect ${getSocialProviderLabel(provider)}.`);

      if (options?.onError) {
        options.onError(errorMessage);
      } else {
        setSocialActionError(errorMessage);
      }
    } finally {
      if (!didRedirect) {
        setSocialActionLoadingProvider(null);
      }
    }
  };

  const syncAuthenticatedUser = async () => {
    if (!token) {
      return;
    }

    const refreshedUser = await authApi.getCurrentUser(token);
    dispatch(setUser(refreshedUser));
  };

  const openProfileNameEditor = () => {
    setProfileNameEditorValue(profile?.profile?.name || user?.name || '');
    setProfileNameError(null);
    setProfileNameValidationError(null);
    setProfileNameSuccess(null);
    setIsProfileNameEditorOpen(true);
  };

  const closeProfileNameEditor = () => {
    if (profileNameSaving) {
      return;
    }

    setIsProfileNameEditorOpen(false);
    setProfileNameError(null);
    setProfileNameValidationError(null);
  };

  const handleProfileNameChange = (value: string) => {
    setProfileNameEditorValue(value);
    setProfileNameError(null);
    setProfileNameValidationError(null);
  };

  const handleProfileNameSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!token || profileNameSaving || profileNameSaveInFlightRef.current) {
      return;
    }

    const normalizedName = profileNameEditorValue.trim();

    if (!normalizedName) {
      setProfileNameValidationError('Enter a name to display on your profile.');
      return;
    }

    if (normalizedName.length > PROFILE_NAME_MAX_LENGTH) {
      setProfileNameValidationError(`Name must be ${PROFILE_NAME_MAX_LENGTH} characters or fewer.`);
      return;
    }

    if (normalizedName === (profile?.profile?.name || user?.name || '').trim()) {
      setIsProfileNameEditorOpen(false);
      return;
    }

    profileNameSaveInFlightRef.current = true;
    setProfileNameSaving(true);
    setProfileNameError(null);
    setProfileNameValidationError(null);

    try {
      const nextProfile = await profileApi.updateMyProfile(token, {name: normalizedName});
      setProfile(nextProfile);

      if (user) {
        dispatch(setUser({
          ...user,
          name: nextProfile.profile.name,
        }));
      }

      setProfileNameEditorValue(nextProfile.profile.name);
      setIsProfileNameEditorOpen(false);
      setProfileNameSuccess('Profile name updated successfully.');
    } catch (saveError) {
      setProfileNameError(getMutationErrorMessage(saveError, 'Failed to update profile name.'));
    } finally {
      profileNameSaveInFlightRef.current = false;
      setProfileNameSaving(false);
    }
  };

  const openProfilePicturePicker = () => {
    setProfilePictureError(null);
    setProfilePictureSuccess(null);
    setIsProfilePictureSourceOpen(true);
  };

  const openNativeProfilePictureCamera = () => {
    setIsProfilePictureSourceOpen(false);
    profilePictureCameraInputRef.current?.click();
  };

  const openProfilePictureLibrary = () => {
    setIsProfilePictureSourceOpen(false);
    profilePictureLibraryInputRef.current?.click();
  };

  const closeProfilePictureEditor = () => {
    if (profilePictureLoading) {
      return;
    }

    setProfilePictureEditor(null);
    setProfilePictureError(null);
  };

  const prepareProfilePictureForEditing = async (file: File) => {
    if (!token) {
      return;
    }

    setProfilePictureError(null);
    setProfilePictureSuccess(null);

    if (!PROFILE_PICTURE_ALLOWED_TYPES.includes(file.type)) {
      setProfilePictureError('Only JPG, PNG, and WEBP images are supported.');
      return;
    }

    if (file.size > PROFILE_PICTURE_MAX_FILE_SIZE_BYTES) {
      setProfilePictureError('Profile picture size must be 5MB or less.');
      return;
    }

    try {
      const previewUrl = await readImageFileAsDataUrl(file);
      const loadedImage = await loadImageSource(previewUrl);

      setProfilePictureEditor({
        file,
        previewUrl,
        imageWidth: loadedImage.naturalWidth || loadedImage.width,
        imageHeight: loadedImage.naturalHeight || loadedImage.height,
        cropState: {
          x: 50,
          y: 50,
          zoom: 1,
        },
      });
    } catch (selectionError) {
      setProfilePictureError(getMutationErrorMessage(selectionError, 'Failed to prepare this image for editing.'));
    }
  };

  const handleProfilePictureSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    await prepareProfilePictureForEditing(file);
  };

  const handleProfilePictureCaptured = async (file: File) => {
    setIsProfilePictureSourceOpen(false);
    await prepareProfilePictureForEditing(file);
  };

  const handleProfilePictureCropChange = (nextCropState: ProfilePictureCropState) => {
    setProfilePictureEditor((currentEditorState) => {
      if (!currentEditorState) {
        return currentEditorState;
      }

      return {
        ...currentEditorState,
        cropState: nextCropState,
      };
    });
    setProfilePictureError(null);
  };

  const handleProfilePictureSave = async () => {
    if (!token || !profilePictureEditor) {
      return;
    }

    setProfilePictureLoading(true);
    setProfilePictureError(null);
    setProfilePictureSuccess(null);

    try {
      const optimizedProfilePictureFile = await createCroppedProfilePictureFile(
        profilePictureEditor.previewUrl,
        profilePictureEditor.file,
        profilePictureEditor.cropState,
      );
      const nextProfile = await profileApi.updateMyProfilePicture(token, optimizedProfilePictureFile);
      setProfile(nextProfile);
      setProfilePictureEditor(null);

      try {
        await syncAuthenticatedUser();
      } catch (refreshError) {
        if (user) {
          dispatch(setUser({
            ...user,
            name: nextProfile.profile?.name || user.name,
            email: nextProfile.email || user.email,
            avatar: nextProfile.profile?.profilePicture,
          }));
        } else {
          throw refreshError;
        }
      }

      setProfilePictureSuccess('Profile photo updated successfully.');
    } catch (uploadError) {
      setProfilePictureError(getMutationErrorMessage(uploadError, 'Failed to update profile picture'));
    } finally {
      setProfilePictureLoading(false);
    }
  };

  const handleProfilePictureRemove = async () => {
    if (
      !token ||
      profilePictureLoading ||
      profilePictureRemoveInFlightRef.current ||
      !profile?.profile?.profilePicture
    ) {
      return;
    }

    profilePictureRemoveInFlightRef.current = true;
    setProfilePictureLoading(true);
    setProfilePictureError(null);
    setProfilePictureSuccess(null);

    try {
      const nextProfile = await profileApi.removeMyProfilePicture(token);
      setProfile(nextProfile);

      try {
        await syncAuthenticatedUser();
      } catch (refreshError) {
        if (user) {
          dispatch(setUser({
            ...user,
            name: nextProfile.profile?.name || user.name,
            email: nextProfile.email || user.email,
            avatar: nextProfile.profile?.profilePicture,
          }));
        } else {
          throw refreshError;
        }
      }

      setIsProfilePictureSourceOpen(false);
      setProfilePictureSuccess('Profile photo removed successfully.');
    } catch (removeError) {
      setProfilePictureError(getMutationErrorMessage(removeError, 'Failed to remove profile picture'));
    } finally {
      profilePictureRemoveInFlightRef.current = false;
      setProfilePictureLoading(false);
    }
  };

  const handleAthleticFieldChange = (field: keyof ProfileAthleticFormValues, value: string) => {
    setAthleticFormValues((currentValues) => ({
      ...currentValues,
      [field]: value,
    }));
    resetEditorMessages();
  };

  const handleArtisticFieldChange = (field: keyof ProfileArtisticFormValues, value: string) => {
    setArtisticFormValues((currentValues) => ({
      ...currentValues,
      [field]: value,
    }));
    resetEditorMessages();
  };

  const handleGeneralServiceFieldChange = (
    field: keyof ProfileGeneralServiceFormValues,
    value: string
  ) => {
    setGeneralServiceFormValues((currentValues) => ({
      ...currentValues,
      [field]: value,
    }));
    resetEditorMessages();
  };

  const handleCollegeServiceFieldChange = (
    field: keyof ProfileCollegeServiceFormValues,
    value: string
  ) => {
    setCollegeServiceFormValues((currentValues) => ({
      ...currentValues,
      [field]: value,
    }));
    resetEditorMessages();
  };

  const openCreateEditor = () => {
    setManagerError(null);
    resetEditorMessages();
    setEditorMode('create');
    setEditorTab(activeTab);
    setEditingItemId(null);
    resetFormForTab(activeTab);
    setIsEditorOpen(true);
  };

  const openEditEditor = (cardId: string) => {
    if (!profile) {
      return;
    }

    setManagerError(null);
    resetEditorMessages();
    setEditorMode('edit');
    setEditorTab(activeTab);

    if (activeTab === 'athletic') {
      const service = profile.athleticServices.find((item, index) => getAthleticCardId(item, index) === cardId);
      if (!service?._id) {
        setManagerError('We could not find that athletic experience to edit.');
        return;
      }

      setEditingItemId(service._id);
      setAthleticFormValues(mapAthleticServiceToFormValues(service));
    } else if (activeTab === 'artistic') {
      const experience = profile.artisticExperiences.find((item, index) => getArtisticCardId(item, index) === cardId);
      if (!experience?._id) {
        setManagerError('We could not find that artistic experience to edit.');
        return;
      }

      setEditingItemId(experience._id);
      setArtisticFormValues(mapArtisticExperienceToFormValues(experience));
    } else if (activeTab === 'services') {
      const service = profile.generalServices.find((item, index) => getGeneralServiceCardId(item, index) === cardId);
      if (!service?._id) {
        setManagerError('We could not find that service listing to edit.');
        return;
      }

      setEditingItemId(service._id);
      setGeneralServiceFormValues(mapGeneralServiceToFormValues(service));
    } else {
      const service = profile.collegeServices.find((item, index) => getCollegeServiceCardId(item, index) === cardId);
      if (!service?._id) {
        setManagerError('We could not find that college opportunity to edit.');
        return;
      }

      setEditingItemId(service._id);
      setCollegeServiceFormValues(mapCollegeServiceToFormValues(service));
    }

    setIsEditorOpen(true);
  };

  const handleDeleteItem = async (cardId: string, title: string) => {
    if (!token) {
      setManagerError('You must be signed in to manage profile items.');
      return;
    }

    if (!profile) {
      return;
    }

    let itemId: string | null = null;
    let itemLabel = 'item';

    if (activeTab === 'athletic') {
      const service = profile.athleticServices.find((item, index) => getAthleticCardId(item, index) === cardId);
      itemId = service?._id || null;
      itemLabel = 'athletic experience';
    } else if (activeTab === 'artistic') {
      const experience = profile.artisticExperiences.find((item, index) => getArtisticCardId(item, index) === cardId);
      itemId = experience?._id || null;
      itemLabel = 'artistic experience';
    } else if (activeTab === 'services') {
      const service = profile.generalServices.find((item, index) => getGeneralServiceCardId(item, index) === cardId);
      itemId = service?._id || null;
      itemLabel = 'service listing';
    } else {
      const service = profile.collegeServices.find((item, index) => getCollegeServiceCardId(item, index) === cardId);
      itemId = service?._id || null;
      itemLabel = 'college opportunity';
    }

    if (!itemId) {
      setManagerError(`We could not find that ${itemLabel} to delete.`);
      return;
    }

    const confirmed = window.confirm(`Delete "${title}" from your profile?`);
    if (!confirmed) {
      return;
    }

    setManagerLoading(true);
    setManagerError(null);

    try {
      const nextProfile = activeTab === 'athletic'
        ? await profileApi.deleteAthleticService(token, itemId)
        : activeTab === 'artistic'
          ? await profileApi.deleteArtisticExperience(token, itemId)
          : activeTab === 'services'
            ? await profileApi.deleteGeneralService(token, itemId)
            : await profileApi.deleteCollegeService(token, itemId);

      setProfile(nextProfile);
    } catch (deleteError) {
      setManagerError(getMutationErrorMessage(deleteError, `Failed to delete ${itemLabel}`));
    } finally {
      setManagerLoading(false);
    }
  };

  const handleEditorSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!token) {
      setEditorError('You must be signed in to manage profile items.');
      return;
    }

    resetEditorMessages();
    setManagerError(null);

    try {
      let nextProfile: ProfileApiUser;
      setManagerLoading(true);

      if (editorTab === 'athletic') {
        const athleticValuesResult = buildAthleticValues(athleticFormValues);
        if (athleticValuesResult.error || !athleticValuesResult.payload) {
          setEditorValidationError(athleticValuesResult.error || 'Sport name and skill level are required.');
          setManagerLoading(false);
          return;
        }

        if (editorMode === 'create') {
          const payload = toAthleticCreatePayload(athleticValuesResult.payload);
          nextProfile = await profileApi.addAthleticService(token, payload);
        } else {
          if (!editingItemId) {
            setEditorError('We could not determine which athletic experience to update.');
            return;
          }

          const payload = toAthleticUpdatePayload(athleticValuesResult.payload);
          nextProfile = await profileApi.updateAthleticService(token, editingItemId, payload);
        }
      } else if (editorTab === 'artistic') {
        const artisticValuesResult = buildArtisticValues(artisticFormValues);
        if (artisticValuesResult.error || !artisticValuesResult.payload) {
          setEditorValidationError(artisticValuesResult.error || 'Art form and proficiency level are required.');
          setManagerLoading(false);
          return;
        }

        if (editorMode === 'create') {
          const payload = toArtisticCreatePayload(artisticValuesResult.payload);
          nextProfile = await profileApi.addArtisticExperience(token, payload);
        } else {
          if (!editingItemId) {
            setEditorError('We could not determine which artistic experience to update.');
            return;
          }

          const payload = toArtisticUpdatePayload(artisticValuesResult.payload);
          nextProfile = await profileApi.updateArtisticExperience(token, editingItemId, payload);
        }
      } else if (editorTab === 'services') {
        const generalServiceValuesResult = buildGeneralServiceValues(generalServiceFormValues);
        if (generalServiceValuesResult.error || !generalServiceValuesResult.payload) {
          setEditorValidationError(generalServiceValuesResult.error || 'Service title and description are required.');
          setManagerLoading(false);
          return;
        }

        if (editorMode === 'create') {
          const payload = toGeneralServiceCreatePayload(generalServiceValuesResult.payload);
          nextProfile = await profileApi.addGeneralService(token, payload);
        } else {
          if (!editingItemId) {
            setEditorError('We could not determine which service listing to update.');
            return;
          }

          const payload = toGeneralServiceUpdatePayload(generalServiceValuesResult.payload);
          nextProfile = await profileApi.updateGeneralService(token, editingItemId, payload);
        }
      } else {
        const collegeServiceValuesResult = buildCollegeServiceValues(collegeServiceFormValues);
        if (collegeServiceValuesResult.error || !collegeServiceValuesResult.payload) {
          setEditorValidationError(
            collegeServiceValuesResult.error || 'Opportunity type, title, and description are required.'
          );
          setManagerLoading(false);
          return;
        }

        if (editorMode === 'create') {
          const payload = toCollegeServiceCreatePayload(collegeServiceValuesResult.payload);
          nextProfile = await profileApi.addCollegeService(token, payload);
        } else {
          if (!editingItemId) {
            setEditorError('We could not determine which college opportunity to update.');
            return;
          }

          const payload = toCollegeServiceUpdatePayload(collegeServiceValuesResult.payload);
          nextProfile = await profileApi.updateCollegeService(token, editingItemId, payload);
        }
      }

      setProfile(nextProfile);
      closeEditor();
      resetFormForTab(editorTab);
    } catch (submitError) {
      setEditorError(getMutationErrorMessage(submitError, 'Failed to save profile changes'));
    } finally {
      setManagerLoading(false);
    }
  };

  const handleSocialPrimaryAction = async (provider: ProfileSocialProvider) => {
    const selectedLink = profileSocialLinks.find((link) => link.provider === provider);

    if (!selectedLink) {
      return;
    }

    if (selectedLink.status === 'connected' && selectedLink.href) {
      window.open(selectedLink.href, '_blank', 'noopener,noreferrer');
      return;
    }

    if (provider === 'website') {
      openSocialConnectionModal('website', 'website', selectedLink.href || profile?.socialLinks?.website || '');
      return;
    }

    if (selectedLink.status === 'needs_public_url') {
      if (provider === 'linkedin') {
        await startSocialConnectionRedirect(provider);
        return;
      }

      openSocialConnectionModal(provider, 'public-url', selectedLink.href || '');
      return;
    }

    if (provider === 'facebook' && facebookPagePickerState) {
      setSocialActionError(null);
      setSocialActionSuccess('Choose the Facebook Page you want to connect.');
      setIsFacebookPagePickerOpen(true);
      return;
    }

    await startSocialConnectionRedirect(provider);
  };

  const handleSocialDisconnect = async (provider: ProfileSocialProvider) => {
    if (!token) {
      setSocialActionSuccess(null);
      setSocialActionError('You must be signed in to disconnect social accounts.');
      return;
    }

    const confirmed = window.confirm(`Disconnect ${getSocialProviderLabel(provider)} from your Profile page?`);
    if (!confirmed) {
      return;
    }

    setSocialActionLoadingProvider(provider);
    setSocialActionError(null);
    setSocialActionSuccess(null);

    try {
      const nextProfile = await profileApi.disconnectSocialConnection(token, provider);
      setProfile(nextProfile);
      setSocialActionSuccess(`${getSocialProviderLabel(provider)} disconnected successfully.`);
    } catch (socialError) {
      setSocialActionError(getMutationErrorMessage(socialError, `Failed to disconnect ${getSocialProviderLabel(provider)}.`));
    } finally {
      setSocialActionLoadingProvider(null);
    }
  };

  const handleFacebookPageSelection = async (pageId: string) => {
    if (!token || !facebookPagePickerState) {
      setFacebookPagePickerError('Your Facebook Page selection session is no longer available. Please try again.');
      return;
    }

    setFacebookPagePickerSubmittingPageId(pageId);
    setFacebookPagePickerError(null);
    setSocialActionError(null);
    setSocialActionSuccess(null);

    try {
      const nextProfile = await profileApi.confirmFacebookPageSelection(token, {
        state: facebookPagePickerState,
        pageId,
      });

      setProfile(nextProfile);
      resetFacebookPagePickerSession();
      setSocialActionSuccess('Facebook Page linked successfully.');
    } catch (selectionError) {
      const errorMessage = getMutationErrorMessage(selectionError, 'Failed to connect the selected Facebook Page.');

      if (selectionError instanceof ProfileApiError && selectionError.status === 404) {
        resetFacebookPagePickerSession();
        setSocialActionError(errorMessage);
      } else {
        setFacebookPagePickerError(errorMessage);
      }
    } finally {
      setFacebookPagePickerSubmittingPageId(null);
    }
  };

  const handleFacebookPageCreation = () => {
    setFacebookPageCreationModalError(null);
    window.open('https://www.facebook.com/pages/create', '_blank', 'noopener,noreferrer');
  };

  const handleFacebookPageCreationRetry = async () => {
    setFacebookPageCreationModalError(null);
    await startSocialConnectionRedirect('facebook', {
      onError: setFacebookPageCreationModalError,
    });
  };

  const handleSocialConnectionModalSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!socialConnectionModalProvider) {
      setSocialConnectionModalError('Choose a provider before saving this link.');
      return;
    }

    if (!token) {
      setSocialConnectionModalError('You must be signed in to save this link.');
      return;
    }

    resetSocialModalMessages();
    setSocialActionError(null);
    setSocialActionSuccess(null);

    const validationMessage = validateSocialProfileUrl(socialConnectionModalProvider, socialConnectionModalValue);
    if (validationMessage) {
      setSocialConnectionModalValidationError(validationMessage);
      return;
    }

    const trimmedValue = socialConnectionModalValue.trim();
    setSocialConnectionModalLoading(true);

    try {
      const nextProfile = socialConnectionModalMode === 'website'
        ? await profileApi.saveWebsiteSocialConnection(token, trimmedValue)
        : await profileApi.confirmSocialConnectionPublicUrl(
          token,
          socialConnectionModalProvider as Exclude<ProfileApiSocialProvider, 'website'>,
          trimmedValue
        );

      setProfile(nextProfile);
      forceCloseSocialConnectionModal();
      setSocialActionSuccess(
        socialConnectionModalMode === 'website'
          ? 'Website link saved successfully.'
          : `${getSocialProviderLabel(socialConnectionModalProvider)} linked successfully.`
      );
    } catch (socialError) {
      setSocialConnectionModalError(getMutationErrorMessage(socialError, 'Failed to save this social link.'));
    } finally {
      setSocialConnectionModalLoading(false);
    }
  };

  const currentPlan = profile?.planTier
    || (isOwnProfile ? (user?.planTier || 'free') : 'free');
  const isPro = isProPlanTier(currentPlan);

  const displayName = profile?.profile?.name || (isOwnProfile ? user?.name : undefined) || 'User Name';
  const displayEmail = isOwnProfile ? (profile?.email || user?.email || undefined) : undefined;
  const profileName = profile?.profile?.name || (isOwnProfile ? user?.name : undefined) || 'N/A';
  const profileEmail = isOwnProfile ? (profile?.email || user?.email || undefined) : undefined;
  const memberSince = formatMemberSince(profile?.createdAt);
  const directMessagingTargetUserId = profile?._id || requestedProfileUserId;
  const profileStats = buildProfileStats(profile);
  const profileSocialLinks = buildSocialLinks(profile?.socialConnections, profile?.socialLinks);
  const visibleSocialLinks = isOwnProfile
    ? profileSocialLinks
    : profileSocialLinks.filter((link) => Boolean(link.href));
  const socialConnectionModalCopy = getSocialConnectionModalCopy(
    socialConnectionModalMode,
    socialConnectionModalProvider
  );

  const cardsByTab: Record<ProfileTab, ProfileExperienceCard[]> = {
    athletic: buildAthleticCards(profile?.athleticServices),
    artistic: buildArtisticCards(profile?.artisticExperiences),
    services: buildGeneralServiceCards(profile?.generalServices),
    college: buildCollegeServiceCards(profile?.collegeServices),
  };

  const handleDirectMessagingClick = () => {
    if (isOwnProfile) {
      navigate('/messages');
      return;
    }

    if (directMessagingTargetUserId && directMessagingTargetUserId !== user?.id) {
      navigate(`/messages?recipientId=${encodeURIComponent(directMessagingTargetUserId)}`);
      return;
    }

    navigate('/messages');
  };

  const handlePostCommentsClick = () => {
    navigate('/home');
  };

  const handleFollowSystemClick = () => {
    navigate('/home');
  };

  const handleMediaUploadsClick = () => {
    navigate('/home');
  };

  const handleTalentSearchClick = () => {
    navigate('/discover-talent');
  };

  const loadSafetySettings = async () => {
    if (!token) {
      setSafetySettingsError('You must be signed in to manage safety settings.');
      return;
    }

    setSafetySettingsLoading(true);
    setSafetySettingsError(null);
    setSafetySettingsValidationError(null);
    setSafetySettingsSuccess(null);

    try {
      const [preferences, blockedUsers] = await Promise.all([
        safetyApi.getPreferences(token),
        safetyApi.getBlockedUsers(token),
      ]);

      setSafetyBlockedWordsText(preferences.blockedWords.join('\n'));
      setSafetyBlockObscenity(preferences.blockObscenity);
      setSafetyLocationSharingEnabled(preferences.locationSharingEnabled);
      setSafetyBlockedUsers(blockedUsers);
    } catch (safetyError) {
      setSafetySettingsError(getMutationErrorMessage(safetyError, 'Failed to load safety settings.'));
    } finally {
      setSafetySettingsLoading(false);
    }
  };

  const handleSafetyFeaturesClick = () => {
    if (isOwnProfile) {
      setIsSafetyActionsOpen(false);
      setIsSafetySettingsOpen(true);
      void loadSafetySettings();
      return;
    }

    setSafetyActionError(null);
    setSafetyActionSuccess(null);
    setSafetyReportReason('harassment');
    setSafetyReportDetails('');
    setIsSafetyActionsOpen(true);
  };

  const closeSafetySettingsModal = () => {
    if (safetySettingsLoading || safetySettingsSaving || safetyUnblockingUserId) {
      return;
    }

    setIsSafetySettingsOpen(false);
    setSafetySettingsError(null);
    setSafetySettingsValidationError(null);
    setSafetySettingsSuccess(null);
  };

  const closeSafetyActionsModal = () => {
    if (safetyActionBlocking || safetyActionReporting) {
      return;
    }

    setIsSafetyActionsOpen(false);
    setSafetyActionError(null);
    setSafetyActionSuccess(null);
    setSafetyReportDetails('');
    setSafetyReportReason('harassment');
  };

  const handleSafetySettingsSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!token) {
      setSafetySettingsError('You must be signed in to save safety settings.');
      return;
    }

    setSafetySettingsError(null);
    setSafetySettingsValidationError(null);
    setSafetySettingsSuccess(null);

    const blockedWordsResult = buildSafetyBlockedWords(safetyBlockedWordsText);
    if (blockedWordsResult.error || !blockedWordsResult.payload) {
      setSafetySettingsValidationError(blockedWordsResult.error || 'Blocked words could not be saved.');
      return;
    }

    setSafetySettingsSaving(true);

    try {
      const preferences = await safetyApi.updatePreferences(token, {
        blockedWords: blockedWordsResult.payload,
        blockObscenity: safetyBlockObscenity,
        locationSharingEnabled: safetyLocationSharingEnabled,
      });

      setSafetyBlockedWordsText(preferences.blockedWords.join('\n'));
      setSafetyBlockObscenity(preferences.blockObscenity);
      setSafetyLocationSharingEnabled(preferences.locationSharingEnabled);
      setSafetySettingsSuccess('Safety settings saved.');
    } catch (safetyError) {
      setSafetySettingsError(getMutationErrorMessage(safetyError, 'Failed to save safety settings.'));
    } finally {
      setSafetySettingsSaving(false);
    }
  };

  const handleSafetyUnblockUser = async (blockedUserId: string) => {
    if (!token) {
      setSafetySettingsError('You must be signed in to unblock users.');
      return;
    }

    setSafetyUnblockingUserId(blockedUserId);
    setSafetySettingsError(null);
    setSafetySettingsSuccess(null);

    try {
      await safetyApi.unblockUser(token, blockedUserId);
      setSafetyBlockedUsers((currentBlockedUsers) => (
        currentBlockedUsers.filter((blockedUser) => blockedUser._id !== blockedUserId)
      ));
      setSafetySettingsSuccess('User unblocked.');
    } catch (safetyError) {
      setSafetySettingsError(getMutationErrorMessage(safetyError, 'Failed to unblock this user.'));
    } finally {
      setSafetyUnblockingUserId(null);
    }
  };

  const handleSafetyBlockUser = async () => {
    if (!token) {
      setSafetyActionError('You must be signed in to block users.');
      return;
    }

    if (!directMessagingTargetUserId || directMessagingTargetUserId === user?.id) {
      setSafetyActionError('We could not determine which user to block.');
      return;
    }

    const confirmed = window.confirm(`Block ${displayName}?`);
    if (!confirmed) {
      return;
    }

    setSafetyActionBlocking(true);
    setSafetyActionError(null);
    setSafetyActionSuccess(null);

    try {
      await safetyApi.blockUser(token, directMessagingTargetUserId);
      setSafetyActionSuccess(`${displayName} has been blocked.`);
    } catch (safetyError) {
      setSafetyActionError(getMutationErrorMessage(safetyError, 'Failed to block this user.'));
    } finally {
      setSafetyActionBlocking(false);
    }
  };

  const handleSafetyReportSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!token) {
      setSafetyActionError('You must be signed in to report users.');
      return;
    }

    if (!directMessagingTargetUserId || directMessagingTargetUserId === user?.id) {
      setSafetyActionError('We could not determine which user to report.');
      return;
    }

    setSafetyActionReporting(true);
    setSafetyActionError(null);
    setSafetyActionSuccess(null);

    try {
      await safetyApi.reportUser(token, {
        reportedUserId: directMessagingTargetUserId,
        reason: safetyReportReason,
        additionalDetails: safetyReportDetails.trim() || undefined,
      });
      setSafetyActionSuccess('Report submitted.');
      setSafetyReportDetails('');
    } catch (safetyError) {
      setSafetyActionError(getMutationErrorMessage(safetyError, 'Failed to submit report.'));
    } finally {
      setSafetyActionReporting(false);
    }
  };

  return (
    <div className={styles.pageRoot}>
      <div className={styles.profileCard}>
          {loading ? (
            <div className={styles.profileStateCard}>
              <div className={styles.profileLoadingSpinner} />
              <h3 className={styles.profileStateTitle}>Loading profile</h3>
              <p className={styles.profileStateDescription}>
                {isOwnProfile
                  ? 'We\'re loading your athletic, artistic, service, and opportunity data from the database.'
                  : 'We\'re loading this user\'s athletic, artistic, service, and opportunity data from the database.'}
              </p>
            </div>
          ) : error ? (
            <div className={styles.profileStateCard}>
              <ProfileWarningIcon />
              <h3 className={styles.profileStateTitle}>Unable to load profile</h3>
              <p className={styles.profileStateDescription}>{error}</p>
              <button
                type="button"
                onClick={() => setRetryCount((currentRetryCount) => currentRetryCount + 1)}
                className={styles.profileRetryButton}
              >
                Try Again
              </button>
            </div>
          ) : (
            <>
              <ProfileHeader
                userName={displayName}
                userEmail={displayEmail}
                stats={profileStats}
                avatarUrl={profile?.profile?.profilePicture}
                isPro={isPro}
                isEditable={isOwnProfile}
                profilePictureUploading={profilePictureLoading}
                onChangeProfilePicture={openProfilePicturePicker}
                onEditProfile={openProfileNameEditor}
                onUpgradeToPro={() => {
                  navigate('/settings?upgrade=1');
                }}
              />

              <div className={styles.profileContent}>

              {isOwnProfile ? (
                <ProfileNameEditorModal
                  isOpen={isProfileNameEditorOpen}
                  value={profileNameEditorValue}
                  loading={profileNameSaving}
                  error={profileNameError}
                  validationError={profileNameValidationError}
                  onChange={handleProfileNameChange}
                  onClose={closeProfileNameEditor}
                  onSubmit={handleProfileNameSubmit}
                />
              ) : null}

              {isOwnProfile ? (
                <>
                  <input
                    ref={profilePictureCameraInputRef}
                    type="file"
                    accept={PROFILE_PICTURE_ALLOWED_TYPES.join(',')}
                    capture="user"
                    onChange={handleProfilePictureSelected}
                    aria-label="Take a profile photo"
                    hidden
                  />
                  <input
                    ref={profilePictureLibraryInputRef}
                    type="file"
                    accept={PROFILE_PICTURE_ALLOWED_TYPES.join(',')}
                    onChange={handleProfilePictureSelected}
                    aria-label="Choose a profile photo"
                    hidden
                  />
                </>
              ) : null}

              {isOwnProfile && isProfilePictureSourceOpen ? (
                <ProfilePictureSourceModal
                  hasProfilePhoto={Boolean(profile?.profile?.profilePicture)}
                  isRemovingPhoto={profilePictureLoading}
                  removePhotoError={profilePictureError}
                  onClose={() => setIsProfilePictureSourceOpen(false)}
                  onNativeCameraFallback={openNativeProfilePictureCamera}
                  onPhotoCaptured={handleProfilePictureCaptured}
                  onRemovePhoto={handleProfilePictureRemove}
                  onUploadPhoto={openProfilePictureLibrary}
                />
              ) : null}

              {isOwnProfile && profilePictureEditor ? (
                <ProfilePictureEditorModal
                  imageSrc={profilePictureEditor.previewUrl}
                  imageWidth={profilePictureEditor.imageWidth}
                  imageHeight={profilePictureEditor.imageHeight}
                  cropState={profilePictureEditor.cropState}
                  error={profilePictureError}
                  isSaving={profilePictureLoading}
                  onCancel={closeProfilePictureEditor}
                  onSave={handleProfilePictureSave}
                  onChangeCropState={handleProfilePictureCropChange}
                />
              ) : null}

              {isOwnProfile && profilePictureSuccess ? (
                <div className={`${styles.profilePictureMessage} ${styles.profilePictureMessageSuccess}`}>
                  {profilePictureSuccess}
                </div>
              ) : null}

              {isOwnProfile && profileNameSuccess ? (
                <div
                  className={`${styles.profilePictureMessage} ${styles.profilePictureMessageSuccess}`}
                  role="status"
                >
                  {profileNameSuccess}
                </div>
              ) : null}

              {isOwnProfile && profilePictureError ? (
                <div className={`${styles.profilePictureMessage} ${styles.profilePictureMessageError}`}>
                  {profilePictureError}
                </div>
              ) : null}

              <div className={`${styles.profileSectionShell} ${styles.profileSectionShellFirst}`.trim()}>
                <ProfileInformationSection
                  name={profileName}
                  email={profileEmail}
                  memberSince={memberSince}
                  isEditable={isOwnProfile}
                  onEdit={openProfileNameEditor}
                />
              </div>

              {isOwnProfile && socialActionSuccess ? (
                <div className={styles.socialMessageSuccess}>{socialActionSuccess}</div>
              ) : null}

              {isOwnProfile && socialActionError ? (
                <div className={styles.socialMessageError}>{socialActionError}</div>
              ) : null}

              <div className={styles.profileSectionShell}>
                <ProfileSocialLinksSection
                  links={visibleSocialLinks}
                  isEditable={isOwnProfile}
                  loadingProvider={isOwnProfile ? socialActionLoadingProvider : null}
                  onPrimaryAction={handleSocialPrimaryAction}
                  onDisconnect={handleSocialDisconnect}
                />
              </div>

              <div className={styles.profileSectionShell}>
                <ProfileServicesSection
                  activeTab={activeTab}
                  tabOptions={availableProfileTabs}
                  cardsByTab={cardsByTab}
                  managerError={isOwnProfile ? managerError : null}
                  actionsDisabled={isOwnProfile ? managerLoading : false}
                  isEditable={isOwnProfile}
                  title={isOwnProfile ? 'My Services & Experiences' : 'Services & Experiences'}
                  onAddItem={openCreateEditor}
                  onEditItem={openEditEditor}
                  onDeleteItem={handleDeleteItem}
                  onTabChange={setActiveTab}
                />
              </div>

              {isOwnProfile ? (
                <ProfileServiceEditorModal
                  isOpen={isEditorOpen}
                  tab={editorTab}
                  mode={editorMode}
                  loading={managerLoading}
                  error={editorError}
                  validationError={editorValidationError}
                  athleticValues={athleticFormValues}
                  artisticValues={artisticFormValues}
                  generalServiceValues={generalServiceFormValues}
                  collegeServiceValues={collegeServiceFormValues}
                  onClose={closeEditor}
                  onAthleticChange={handleAthleticFieldChange}
                  onArtisticChange={handleArtisticFieldChange}
                  onGeneralServiceChange={handleGeneralServiceFieldChange}
                  onCollegeServiceChange={handleCollegeServiceFieldChange}
                  onSubmit={handleEditorSubmit}
                />
              ) : null}

              {isOwnProfile ? (
                <ProfileFacebookPageCreationModal
                  isOpen={isFacebookPageCreationModalOpen}
                  description={facebookPageCreationModalDescription}
                  loading={socialActionLoadingProvider === 'facebook'}
                  error={facebookPageCreationModalError}
                  onClose={closeFacebookPageCreationModal}
                  onCreatePage={handleFacebookPageCreation}
                  onRetry={handleFacebookPageCreationRetry}
                />
              ) : null}

              {isOwnProfile ? (
                <ProfileFacebookPagePickerModal
                  isOpen={isFacebookPagePickerOpen}
                  pages={facebookPageOptions}
                  loading={facebookPagePickerLoading}
                  submittingPageId={facebookPagePickerSubmittingPageId}
                  error={facebookPagePickerError}
                  onClose={closeFacebookPagePickerModal}
                  onSelect={handleFacebookPageSelection}
                />
              ) : null}

              {isOwnProfile ? (
                <ProfileSocialConnectionModal
                  isOpen={isSocialConnectionModalOpen}
                  provider={socialConnectionModalProvider}
                  title={socialConnectionModalCopy.title}
                  description={socialConnectionModalCopy.description}
                  value={socialConnectionModalValue}
                  submitLabel={socialConnectionModalCopy.submitLabel}
                  loading={socialConnectionModalLoading}
                  error={socialConnectionModalError}
                  validationError={socialConnectionModalValidationError}
                  onChange={(value) => {
                    setSocialConnectionModalValue(value);
                    resetSocialModalMessages();
                  }}
                  onClose={closeSocialConnectionModal}
                  onSubmit={handleSocialConnectionModalSubmit}
                />
              ) : null}

              <div className={styles.profileSectionShell}>
                <ProfileFeaturesSection
                  features={PROFILE_FEATURE_CARDS}
                  onDirectMessagingClick={handleDirectMessagingClick}
                  onPostCommentsClick={handlePostCommentsClick}
                  onFollowSystemClick={handleFollowSystemClick}
                  onMediaUploadsClick={handleMediaUploadsClick}
                  onTalentSearchClick={handleTalentSearchClick}
                  onSafetyFeaturesClick={handleSafetyFeaturesClick}
                />
              </div>

              <ProfileSafetySettingsModal
                isOpen={isOwnProfile && isSafetySettingsOpen}
                blockedWordsText={safetyBlockedWordsText}
                blockObscenity={safetyBlockObscenity}
                locationSharingEnabled={safetyLocationSharingEnabled}
                blockedUsers={safetyBlockedUsers}
                loading={safetySettingsLoading}
                saving={safetySettingsSaving}
                unblockingUserId={safetyUnblockingUserId}
                error={safetySettingsError}
                validationError={safetySettingsValidationError}
                success={safetySettingsSuccess}
                onBlockedWordsChange={(value) => {
                  setSafetyBlockedWordsText(value);
                  setSafetySettingsValidationError(null);
                  setSafetySettingsError(null);
                  setSafetySettingsSuccess(null);
                }}
                onBlockObscenityChange={(value) => {
                  setSafetyBlockObscenity(value);
                  setSafetySettingsError(null);
                  setSafetySettingsSuccess(null);
                }}
                onLocationSharingChange={(value) => {
                  setSafetyLocationSharingEnabled(value);
                  setSafetySettingsError(null);
                  setSafetySettingsSuccess(null);
                }}
                onUnblockUser={handleSafetyUnblockUser}
                onClose={closeSafetySettingsModal}
                onSubmit={handleSafetySettingsSubmit}
              />

              <ProfileSafetyActionsModal
                isOpen={!isOwnProfile && isSafetyActionsOpen}
                targetName={displayName}
                reportReason={safetyReportReason}
                reportDetails={safetyReportDetails}
                blocking={safetyActionBlocking}
                reporting={safetyActionReporting}
                error={safetyActionError}
                success={safetyActionSuccess}
                onReportReasonChange={(reason) => {
                  setSafetyReportReason(reason);
                  setSafetyActionError(null);
                  setSafetyActionSuccess(null);
                }}
                onReportDetailsChange={(details) => {
                  setSafetyReportDetails(details);
                  setSafetyActionError(null);
                  setSafetyActionSuccess(null);
                }}
                onBlockUser={handleSafetyBlockUser}
                onClose={closeSafetyActionsModal}
                onReportSubmit={handleSafetyReportSubmit}
              />
              </div>
            </>
          )}
      </div>
    </div>
  );
};

export default Profile;
